export const M4_UNIT_1 = {
  id: "m4-u1-talking-about-yourself",
  title: "Unidad 1: Introducing Yourself",
  topic: "Master professional introductions! Learn to present yourself with confidence using 'to be' for names, ages, nationalities, and jobs. Essential for job interviews, networking, and making a great first impression in Canada!",
  iconType: "user",
  grammarImage: "/grammar/grammar-m4-u1.jpg",
  grammarText: "Verbo 'to be' para presentarte: I am [name/age/nationality/job]. Diferencia clave: 'I am' para identidad, NO uses 'do/does' con 'to be'. Contextos formales e informales.",
  
  sets: [
    // SET 1: Basic Introductions with Full Names
    {
      id: "m4-u1-set1",
      title: "Set 1: Professional Names",
      questions: [
        {
          type: "mc",
          q: "En una entrevista de trabajo: 'Hi, I ___ Carlos Mendoza, nice to meet you.'",
          options: ["am", "do", "is"],
          a: "am",
          tip: "I + AM! 'To be' for names, NOT 'do'! Common mistake: I do Carlos ❌"
        },
        {
          type: "input",
          q: "Complete (formal): 'My full name ___ Sandra Patricia Rodriguez.'",
          a: "is",
          successtip: "✓ IS! Professional! 💼 'My name is' = formal interviews! Canadians love full introductions!"
        },
        {
          type: "order",
          q: "Ordena: 'Soy Maria Lopez, encantada de conocerte'",
          words: ["I", "am", "Maria", "Lopez,", "nice", "to", "meet", "you."],
          a: "I am Maria Lopez, nice to meet you.",
          tip: "'I am' + full name + greeting. Order matters in English!"
        },
        {
          type: "mc",
          q: "Tu compañero pregunta: 'What's your name?' Respondes: '___'",
          options: ["I'm Alex", "I do Alex", "My am Alex"],
          a: "I'm Alex",
          successtip: "✓ I'M! Contraction! 🗣️ 'I'm' = casual, 'I am' = formal. Both correct, know when to use!"
        },
        {
          type: "match",
          q: "Match the introduction style to context:",
          pairs: [
            { left: "I'm Juan", right: "Casual (friends)" },
            { left: "I am Juan Rodriguez", right: "Formal (interview)" },
            { left: "My name is Juan", right: "Semi-formal (networking)" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Completa: 'Hi! My first name ___ Roberto.'",
          a: "is",
          tip: "'My name IS' - always 'is' with 'my name', even if you're talking about yourself!"
        },
        {
          type: "mc",
          q: "ERROR: 'I do Maria' ¿Por qué está mal?",
          options: [
            "Se usa 'am', no 'do' con nombres",
            "Falta el apellido",
            "Debe ser 'I does'"
          ],
          a: "Se usa 'am', no 'do' con nombres",
          successtip: "✓ CLASSIC ERROR! 🚫 Spanish speakers mix 'do' with identity! I AM = identity, I DO = actions!"
        },
        {
          type: "order",
          q: "Ordena (formal): 'Mi nombre completo es Ana Patricia Gomez'",
          words: ["My", "full", "name", "is", "Ana", "Patricia", "Gomez."],
          a: "My full name is Ana Patricia Gomez.",
          tip: "'Full name' = nombre completo. Professional contexts love this!"
        },
        {
          type: "input",
          q: "You meet your boss: 'Hello, I ___ David Chen, the new accountant.'",
          a: "am",
          successtip: "✓ AM! Boss language! 👔 Name + job title = professional power intro! Canadian workplace standard!"
        },
        {
          type: "mc",
          q: "¿Cuál es INCORRECTA?",
          options: [
            "I'm Sarah",
            "My name is Sarah",
            "I do Sarah"
          ],
          a: "I do Sarah",
          tip: "'I do' is for ACTIONS (I do homework), NOT identity! ❌"
        }
      ]
    },

    // SET 2: Names in Different Contexts
    {
      id: "m4-u1-set2",
      title: "Set 2: Contexts & Titles",
      questions: [
        {
          type: "mc",
          q: "En un evento de networking: 'Hi! I ___ Dr. Patel, a family doctor.'",
          options: ["am", "is", "do"],
          a: "am",
          successtip: "✓ DR. PATEL! Title power! 🩺 Canadians use titles professionally: Dr., Mr., Ms., Eng. Respect!"
        },
        {
          type: "input",
          q: "Cliente formal: 'My name ___ Mr. Rodriguez, I have an appointment.'",
          a: "is",
          tip: "'Mr./Ms./Mrs.' = formal titles. Use 'is' with 'my name', always!"
        },
        {
          type: "order",
          q: "Ordena: 'Soy la profesora Martinez'",
          words: ["I", "am", "Ms.", "Martinez,", "the", "teacher."],
          a: "I am Ms. Martinez, the teacher.",
          successtip: "✓ MS.! Gender-neutral! 💼 Canadian workplaces prefer 'Ms.' over 'Miss/Mrs.' Professional!"
        },
        {
          type: "match",
          q: "Match person to their introduction:",
          pairs: [
            { left: "Doctor", right: "I'm Dr. Lee" },
            { left: "Engineer", right: "I'm Eng. Santos" },
            { left: "Student", right: "I'm Alex" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "Tu nombre es José but Canadians can't pronounce it. You say: 'I ___ José, but call me Joe.'",
          options: ["am", "is", "do"],
          a: "am",
          tip: "Real immigrant tip! Many adapt their names for easier pronunciation. It's okay! 🌎"
        },
        {
          type: "input",
          q: "Presentación casual: 'Hey! I ___ Mike, short for Michael.'",
          a: "am",
          successtip: "✓ NICKNAME! Canadian culture! 🍁 Nicknames are super common: Mike, Liz, Alex, Sam. Casual = connection!"
        },
        {
          type: "order",
          q: "Ordena (first day at work): 'Soy el nuevo empleado, mi nombre es Carlos'",
          words: ["I", "am", "the", "new", "employee,", "my", "name", "is", "Carlos."],
          a: "I am the new employee, my name is Carlos.",
          tip: "First day power move! Role + name = complete introduction!"
        },
        {
          type: "mc",
          q: "ERROR de hispanohablante: '¿I do Patricia?' ¿Corrección?",
          options: ["I am Patricia", "I is Patricia", "I Patricia"],
          a: "I am Patricia",
          successtip: "✓ I AM! Verb required! 📚 English ALWAYS needs verb: I am/I'm. Spanish 'Soy Patricia' = one word, English = two!"
        },
        {
          type: "input",
          q: "Phone call formal: 'Hello, this ___ Jennifer calling from TD Bank.'",
          a: "is",
          tip: "'This is' for phone introductions! Professional standard! 📞"
        },
        {
          type: "mc",
          q: "Mejor opción para email profesional:",
          options: [
            "My name is Maria Gonzalez",
            "I'm Maria",
            "I do Maria Gonzalez"
          ],
          a: "My name is Maria Gonzalez",
          successtip: "✓ FULL NAME! Email standard! 📧 Professional emails = 'My name is [Full Name]'. First impression in writing!"
        }
      ]
    },

    // SET 3: Ages in Context
    {
      id: "m4-u1-set3",
      title: "Set 3: Talking About Age",
      questions: [
        {
          type: "mc",
          q: "Job application: 'I ___ 28 years old and I have 5 years of experience.'",
          options: ["am", "have", "do"],
          a: "am",
          tip: "'I AM' + age! NOT 'I have'! Spanish 'Tengo 28' ≠ English structure!"
        },
        {
          type: "input",
          q: "Conversación casual: 'How old are you?' - 'I ___ 34.'",
          a: "am",
          successtip: "✓ AM 34! Short answer! 🎂 You can drop 'years old' in casual talk. 'I'm 34' = natural!"
        },
        {
          type: "order",
          q: "Ordena: 'Mi hijo tiene 5 años'",
          words: ["My", "son", "is", "5", "years", "old."],
          a: "My son is 5 years old.",
          tip: "'IS' for third person (he/she/it). He IS, She IS, always!"
        },
        {
          type: "mc",
          q: "ERROR típico: 'I have 30 years' - ¿Por qué está mal?",
          options: [
            "Se dice 'I am 30 years old'",
            "Debe ser 'I has 30 years'",
            "Falta 'of'"
          ],
          a: "Se dice 'I am 30 years old'",
          successtip: "✓ CLASSIC! Spanish trap! 🪤 'Tengo 30 años' = I HAVE, but English = I AM! Different logic!"
        },
        {
          type: "match",
          q: "Match age expressions to situations:",
          pairs: [
            { left: "I'm 25", right: "Casual conversation" },
            { left: "I am 25 years old", right: "Formal document" },
            { left: "She's 40", right: "Talking about someone" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Doctor's office: 'My daughter ___ 12 years old.'",
          a: "is",
          tip: "'My daughter' = she = IS. Third person singular!"
        },
        {
          type: "mc",
          q: "Formulario de inmigración asks: 'Age' - You write: '___'",
          options: ["35 years old", "I am 35", "I have 35"],
          a: "35 years old",
          successtip: "✓ FORMS! Canadian bureaucracy! 📋 Forms want just the fact: '35 years old', no 'I am'!"
        },
        {
          type: "order",
          q: "Ordena: '¿Cuántos años tienes?'",
          words: ["How", "old", "are", "you?"],
          a: "How old are you?",
          tip: "'How old' = ¿Cuántos años? Fixed expression, memorize it!"
        },
        {
          type: "input",
          q: "Talking about parents: 'My mom ___ 62 and my dad ___ 65.'",
          a: "is, is",
          tip: "Both need 'is'! Don't skip verbs in English! Every clause needs a verb!"
        },
        {
          type: "mc",
          q: "Respuesta natural a 'How old are you?':",
          options: ["I'm 29", "I have 29", "I do 29 years"],
          a: "I'm 29",
          successtip: "✓ I'M 29! Natural flow! 🗣️ Native speakers drop 'years old' in conversation. Sounds more fluent!"
        }
      ]
    },

    // SET 4: Nationalities
    {
      id: "m4-u1-set4",
      title: "Set 4: Nationalities & Origins",
      questions: [
        {
          type: "mc",
          q: "Immigration officer asks: 'Where are you from?' - You: 'I ___ from Mexico.'",
          options: ["am", "is", "do"],
          a: "am",
          tip: "'I am FROM' + country. Origin = to be + from!"
        },
        {
          type: "input",
          q: "Networking event: 'I ___ Colombian, but I live in Toronto now.'",
          a: "am",
          successtip: "✓ AM COLOMBIAN! Identity! 🇨🇴 Nationality = adjective with 'to be'. Colombian, Mexican, Canadian!"
        },
        {
          type: "order",
          q: "Ordena: 'Soy de Brasil pero vivo en Canada'",
          words: ["I", "am", "from", "Brazil,", "but", "I", "live", "in", "Canada."],
          a: "I am from Brazil, but I live in Canada.",
          tip: "'I am from' (origin) vs 'I live in' (current location). Different verbs!"
        },
        {
          type: "match",
          q: "Match country to nationality:",
          pairs: [
            { left: "Mexico", right: "Mexican" },
            { left: "Philippines", right: "Filipino/Filipina" },
            { left: "India", right: "Indian" }
          ],
          a: "completed",
          successtip: "✓ TOP IMMIGRANT GROUPS! 🌍 These are the biggest communities in Canada! You're not alone!"
        },
        {
          type: "mc",
          q: "ERROR: 'I am from Colombian' - ¿Corrección?",
          options: [
            "I am Colombian OR I am from Colombia",
            "I am from Colombia people",
            "I do from Colombia"
          ],
          a: "I am Colombian OR I am from Colombia",
          tip: "Two ways! 'I am FROM + country' OR 'I am + nationality'. Both correct!"
        },
        {
          type: "input",
          q: "Job interview: 'I ___ originally from Peru, but I ___ a Canadian resident now.'",
          a: "am, am",
          successtip: "✓ DOUBLE AM! Immigration story! 🍁 'Originally from' + 'now' = your journey! Canadians respect it!"
        },
        {
          type: "order",
          q: "Ordena: 'Mi esposa es filipina'",
          words: ["My", "wife", "is", "Filipina."],
          a: "My wife is Filipina.",
          tip: "Filipina = female, Filipino = male. Gender-specific nationality!"
        },
        {
          type: "mc",
          q: "Meeting neighbors: 'We ___ from Guatemala, we moved here last year.'",
          options: ["are", "is", "do"],
          a: "are",
          tip: "'We' = plural = ARE. I am, you are, we are, they are!"
        },
        {
          type: "input",
          q: "Citizenship ceremony: 'Today I ___ officially Canadian!'",
          a: "am",
          successtip: "✓ CITIZENSHIP! Dream moment! 🎉🇨🇦 From immigrant to Canadian! Proud identity!"
        },
        {
          type: "mc",
          q: "¿Cuál está CORRECTA?",
          options: [
            "She is from China",
            "She is from Chinese",
            "She from China"
          ],
          a: "She is from China",
          tip: "'FROM' + country name (China), NOT nationality (Chinese)!"
        }
      ]
    },

    // SET 5: Jobs & Professions
    {
      id: "m4-u1-set5",
      title: "Set 5: Jobs & Workplaces",
      questions: [
        {
          type: "mc",
          q: "Job interview: 'I ___ a certified welder with 10 years of experience.'",
          options: ["am", "do", "have"],
          a: "am",
          tip: "'I AM' + job title! Identity! Welder = soldador (skilled trade, great in Canada!)"
        },
        {
          type: "input",
          q: "Networking: 'I ___ a registered nurse at Mount Sinai Hospital.'",
          a: "am",
          successtip: "✓ REGISTERED NURSE! Gold job! 🏥 RN = high demand in Canada! Great career path!"
        },
        {
          type: "order",
          q: "Ordena: 'Soy conductor de camión'",
          words: ["I", "am", "a", "truck", "driver."],
          a: "I am a truck driver.",
          tip: "'A' before job! I am A teacher, A driver, A chef. Article required!"
        },
        {
          type: "match",
          q: "Match job to typical workplace:",
          pairs: [
            { left: "I'm a chef", right: "I work in a restaurant" },
            { left: "I'm a mechanic", right: "I work in a garage" },
            { left: "I'm a teacher", right: "I work at a school" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "ERROR: 'I am engineer' - ¿Qué falta?",
          options: ["'A' or 'AN'", "El apellido", "El verbo 'do'"],
          a: "'A' or 'AN'",
          successtip: "✓ ARTICLE! Spanish trap! 🪤 'Soy ingeniero' = no article, but English = 'I am AN engineer'. Always!"
        },
        {
          type: "input",
          q: "First day: 'Hi everyone, I ___ the new warehouse supervisor.'",
          a: "am",
          tip: "'THE' with unique roles (the manager, the supervisor, the boss). Shows authority!"
        },
        {
          type: "order",
          q: "Ordena: 'Mi hermano es electricista certificado'",
          words: ["My", "brother", "is", "a", "certified", "electrician."],
          a: "My brother is a certified electrician.",
          successtip: "✓ CERTIFIED! Professional! ⚡ Certifications matter in Canada! Red Seal trades = great money!"
        },
        {
          type: "mc",
          q: "Tim Hortons application: 'I ___ looking for a position as a barista.'",
          options: ["am", "is", "do"],
          a: "am",
          tip: "'I am looking for' = present continuous for active job search. Common phrase!"
        },
        {
          type: "input",
          q: "LinkedIn profile: 'I ___ an experienced software developer specializing in React.'",
          a: "am",
          successtip: "✓ TECH JOBS! Canadian dream! 💻 IT = high-paying, work from home, great for immigrants!"
        },
        {
          type: "mc",
          q: "Multiple jobs reality: 'I ___ a delivery driver during the day and I ___ a security guard at night.'",
          options: ["am, am", "am, do", "do, do"],
          a: "am, am",
          successtip: "✓ HUSTLE! Immigrant grind! 💪 Two jobs = common immigrant reality. Survival mode! You got this!"
        }
      ]
    },

    // SET 6: Jobs in Detail
    {
      id: "m4-u1-set6",
      title: "Set 6: Professional Details",
      questions: [
        {
          type: "mc",
          q: "Resume: 'I ___ a bilingual customer service representative.'",
          options: ["am", "is", "have"],
          a: "am",
          tip: "'Bilingual' = huge advantage in Canada! Highlight it! French/English or English/Spanish!"
        },
        {
          type: "input",
          q: "Interview: 'I ___ currently working as a line cook at Swiss Chalet.'",
          a: "am",
          successtip: "✓ CURRENTLY WORKING! Present continuous! 🍽️ 'Am working' = active employment NOW! Interview gold!"
        },
        {
          type: "order",
          q: "Ordena: 'Soy un trabajador confiable y puntual'",
          words: ["I", "am", "a", "reliable", "and", "punctual", "worker."],
          a: "I am a reliable and punctual worker.",
          tip: "Adjectives BEFORE noun in English! Reliable worker ✓, Worker reliable ✗"
        },
        {
          type: "match",
          q: "Match survival jobs to descriptions:",
          pairs: [
            { left: "Warehouse worker", right: "I load and unload trucks" },
            { left: "Food delivery", right: "I deliver for Uber Eats" },
            { left: "Cleaner", right: "I work in commercial cleaning" }
          ],
          a: "completed",
          successtip: "✓ SURVIVAL JOBS! Immigrant start! 🚚 These jobs = quick start, no credential barriers. Everyone's story!"
        },
        {
          type: "mc",
          q: "Talking about career: 'In Mexico I ___ a lawyer, but here I ___ studying for Canadian certification.'",
          options: ["was, am", "am, am", "was, was"],
          a: "was, am",
          tip: "'WAS' = past (in Mexico), 'AM' = present (here now). Time changes verb!"
        },
        {
          type: "input",
          q: "Explaining job: 'I ___ a personal support worker, I help elderly people.'",
          a: "am",
          tip: "PSW = Personal Support Worker. High demand in Canada! Healthcare path!"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy buscando trabajo en construcción'",
          words: ["I", "am", "looking", "for", "work", "in", "construction."],
          a: "I am looking for work in construction.",
          successtip: "✓ CONSTRUCTION! Booming industry! 🏗️ Canada needs builders! Good pay, benefits, opportunities!"
        },
        {
          type: "mc",
          q: "ERROR: 'I am do cleaning jobs' - Corrección:",
          options: ["I do cleaning jobs OR I am a cleaner", "I am doing clean", "I do am cleaner"],
          a: "I do cleaning jobs OR I am a cleaner",
          tip: "Don't mix! 'I AM' (identity) OR 'I DO' (action). Not both together!"
        },
        {
          type: "input",
          q: "Career goal: 'My dream ___ to become a homeowner and have my own business.'",
          a: "is",
          successtip: "✓ CANADIAN DREAM! 🏠💼 'My dream IS' - third person! Homeownership + business = ultimate immigrant goal!"
        },
        {
          type: "mc",
          q: "Correct professional intro:",
          options: [
            "I'm Maria, I'm a dental assistant",
            "I do Maria, I do dental work",
            "I am Maria, I do a dental assistant"
          ],
          a: "I'm Maria, I'm a dental assistant",
          tip: "Name = I AM, Job = I AM. Same verb for identity!"
        }
      ]
    },

    // SET 7: Complex Combinations
    {
      id: "m4-u1-set7",
      title: "Set 7: Combining Information",
      questions: [
        {
          type: "mc",
          q: "Complete intro: 'I ___ Carlos, I ___ 32 years old, and I ___ from Colombia.'",
          options: ["am, am, am", "am, have, am", "am, am, do"],
          a: "am, am, am",
          tip: "All three use 'AM'! Name, age, origin = all identity = all 'to be'!"
        },
        {
          type: "input",
          q: "Job fair: 'I ___ Patricia Ruiz, I ___ 28, and I ___ a certified accountant.'",
          a: "am, am, am",
          successtip: "✓ TRIPLE AM! Complete intro! 💼 Name + age + job = professional power introduction! Interview win!"
        },
        {
          type: "order",
          q: "Ordena: 'Soy Juan, tengo 35 años, soy mexicano y soy chef'",
          words: ["I", "am", "Juan,", "I", "am", "35", "years", "old,", "I", "am", "Mexican,", "and", "I", "am", "a", "chef."],
          a: "I am Juan, I am 35 years old, I am Mexican, and I am a chef.",
          tip: "Long sentence! Four 'I am' clauses connected. Commas + 'and' for the last one!"
        },
        {
          type: "match",
          q: "Match question to answer type:",
          pairs: [
            { left: "What's your name?", right: "I am Maria" },
            { left: "How old are you?", right: "I am 30" },
            { left: "Where are you from?", right: "I am from Peru" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "ERROR común: 'I am David, I have 40 years, and I am from Guatemala' - ¿Qué está mal?",
          options: [
            "'I have 40 years' debe ser 'I am 40 years old'",
            "Falta 'the' antes de Guatemala",
            "Debe ser 'I do from Guatemala'"
          ],
          a: "'I have 40 years' debe ser 'I am 40 years old'",
          successtip: "✓ CAUGHT IT! Spanish interference! 🚫 'Tengo años' = I HAVE in Spanish, but English = I AM! Watch this!"
        },
        {
          type: "input",
          q: "Networking (completo): 'Hi, I ___ Roberto, I ___ originally from El Salvador, and I ___ now a permanent resident here in Vancouver.'",
          a: "am, am, am",
          tip: "Three different contexts, same verb! 'Am' works for all identity statements!"
        },
        {
          type: "order",
          q: "Ordena: 'Ella es Ana, tiene 45 años y es gerente'",
          words: ["She", "is", "Ana,", "she", "is", "45", "years", "old,", "and", "she", "is", "a", "manager."],
          a: "She is Ana, she is 45 years old, and she is a manager.",
          tip: "Third person = 'IS' everywhere! He is, she is, it is!"
        },
        {
          type: "mc",
          q: "Immigration interview: 'My wife ___ Sandra, she ___ 33, and she ___ a teacher.'",
          options: ["is, is, is", "is, has, is", "am, is, is"],
          a: "is, is, is",
          successtip: "✓ ALL IS! Third person! 👩‍🏫 'She' = IS for everything. My wife IS = she IS. Consistency!"
        },
        {
          type: "input",
          q: "About your family: 'My kids ___ 8 and 10 years old, and they ___ students at Central Elementary.'",
          a: "are, are",
          tip: "Plural (kids, they) = ARE! I am, he/she is, they ARE!"
        },
        {
          type: "mc",
          q: "Mejor respuesta completa a 'Tell me about yourself':",
          options: [
            "I'm Luis, I'm 29, I'm from Honduras, and I'm a warehouse supervisor",
            "I do Luis, I have 29 years, I from Honduras",
            "My name Luis, my age 29, my country Honduras"
          ],
          a: "I'm Luis, I'm 29, I'm from Honduras, and I'm a warehouse supervisor",
          successtip: "✓ PERFECT INTRO! Interview champion! 🏆 Complete = name + age + origin + job. Hiring managers love structure!"
        }
      ]
    },

    // SET 8: Formal vs Informal
    {
      id: "m4-u1-set8",
      title: "Set 8: Register & Context",
      questions: [
        {
          type: "match",
          q: "Match context to appropriate introduction:",
          pairs: [
            { left: "Job interview", right: "I am Roberto Martinez, and I am 32 years old" },
            { left: "Party with friends", right: "Hey! I'm Rob, I'm 32" },
            { left: "Meeting your landlord", right: "Hello, my name is Roberto Martinez" }
          ],
          a: "completed",
          successtip: "✓ CODE-SWITCHING! Canadian skill! 🎭 Formal/informal = different situations. Read the room!"
        },
        {
          type: "mc",
          q: "Email to HR department: 'Dear Sir/Madam, ___'",
          options: [
            "My name is Sandra Lopez and I am writing to...",
            "Hey! I'm Sandra and I wanna...",
            "I do Sandra Lopez and I write..."
          ],
          a: "My name is Sandra Lopez and I am writing to...",
          tip: "Formal writing = 'My name is' + full name. Professional distance!"
        },
        {
          type: "input",
          q: "Meeting boss's boss: 'Good morning, I ___ Carlos Mendoza, I ___ the new quality control inspector.'",
          a: "am, am",
          tip: "High formality = full sentences, clear structure. Respect through language!"
        },
        {
          type: "order",
          q: "Ordena (very formal): 'Soy el Dr. Ramirez, soy el nuevo cardiólogo'",
          words: ["I", "am", "Dr.", "Ramirez,", "I", "am", "the", "new", "cardiologist."],
          a: "I am Dr. Ramirez, I am the new cardiologist.",
          successtip: "✓ DR. TITLE! Professional respect! 🩺 Medical professionals = always use 'Dr.' in introductions!"
        },
        {
          type: "mc",
          q: "Casual coworker lunch: 'So, where ___ you from originally?'",
          options: ["are", "is", "do"],
          a: "are",
          tip: "Question form! 'Where ARE you from?' - verb before subject in questions!"
        },
        {
          type: "input",
          q: "Texting new friend: 'Hey! I ___ Maria, nice to meet you! 😊'",
          a: "am",
          successtip: "✓ CASUAL! Modern communication! 📱 Texting = contractions OK! 'I'm' perfectly fine here!"
        },
        {
          type: "order",
          q: "Ordena (formal presentation): 'Buenos días, mi nombre es Patricia Fernandez y soy la nueva directora regional'",
          words: ["Good", "morning,", "my", "name", "is", "Patricia", "Fernandez", "and", "I", "am", "the", "new", "regional", "director."],
          a: "Good morning, my name is Patricia Fernandez and I am the new regional director.",
          tip: "Leadership intro = title emphasis! 'THE new director' shows authority!"
        },
        {
          type: "mc",
          q: "Meeting Tim Hortons coworkers (casual): Best option?",
          options: [
            "Hi! I'm Alex, I'm the new guy",
            "Good evening, I am Alexander, I am the new employee",
            "Hey, I do Alex, new person"
          ],
          a: "Hi! I'm Alex, I'm the new guy",
          successtip: "✓ NEW GUY! Canadian slang! 🍩 'New guy/girl' = casual workplace. Tim's = super casual culture!"
        },
        {
          type: "input",
          q: "First day at corporate office: 'Good morning everyone, my name ___ David Chen, and I ___ very excited to join the team.'",
          a: "is, am",
          tip: "'My name IS' (always 'is'), 'I AM' (first person). Different subjects, different forms!"
        },
        {
          type: "mc",
          q: "¿Cuál es demasiado informal para una entrevista?",
          options: [
            "Hey! I'm Mike, I'm 25",
            "Hello, I am Michael, I am 25 years old",
            "Good morning, my name is Michael Smith"
          ],
          a: "Hey! I'm Mike, I'm 25",
          tip: "'Hey!' = too casual! Interviews = 'Hello' or 'Good morning'. First impression matters!"
        }
      ]
    },

    // SET 9: Common Errors
    {
      id: "m4-u1-set9",
      title: "Set 9: Fixing Mistakes",
      questions: [
        {
          type: "mc",
          q: "ERROR: 'I am have 30 years' - ¿Por qué está mal?",
          options: [
            "No puedes usar 'am' y 'have' juntos así",
            "Debe ser 'I am has'",
            "Falta 'old'"
          ],
          a: "No puedes usar 'am' y 'have' juntos así",
          successtip: "✓ MIXING VERBS! Classic error! 🚫 Either 'I AM 30' or 'I HAVE experience', NOT 'am have'!"
        },
        {
          type: "input",
          q: "Corrige: 'I am do Maria from Colombia' → 'I ___ Maria from Colombia.'",
          a: "am",
          tip: "'I am do' = wrong! Choose ONE verb: 'I AM' for identity!"
        },
        {
          type: "order",
          q: "Corrige y ordena: 'I have 25 years and I from Mexico'",
          words: ["I", "am", "25", "years", "old", "and", "I", "am", "from", "Mexico."],
          a: "I am 25 years old and I am from Mexico.",
          tip: "Two errors fixed! 'I have' → 'I am' for age, 'I from' → 'I am from' for origin!"
        },
        {
          type: "match",
          q: "Match error to correction:",
          pairs: [
            { left: "I am engineer", right: "I am AN engineer" },
            { left: "I have 40 years", right: "I am 40 years old" },
            { left: "I from Brazil", right: "I am from Brazil" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "ERROR: 'My name Carlos' - ¿Qué falta?",
          options: ["El verbo 'is'", "El artículo 'a'", "El apellido"],
          a: "El verbo 'is'",
          successtip: "✓ VERB NEEDED! English rule! 📚 Every sentence needs a verb! Spanish 'Mi nombre Carlos' ≠ English!"
        },
        {
          type: "input",
          q: "Corrige: 'She have 33 years and is teacher' → 'She ___ 33 years old and ___ a teacher.'",
          a: "is, is",
          tip: "Two fixes: 'have' → 'is' (age), add 'a' before teacher!"
        },
        {
          type: "order",
          q: "Corrige: 'I do Roberto, I do 28 years, I from Mexico'",
          words: ["I", "am", "Roberto,", "I", "am", "28", "years", "old,", "and", "I", "am", "from", "Mexico."],
          a: "I am Roberto, I am 28 years old, and I am from Mexico.",
          successtip: "✓ ALL FIXED! Triple error! 🔧 'Do' → 'AM' three times! Identity = TO BE, always!"
        },
        {
          type: "mc",
          q: "'I am from Colombian' - Error:",
          options: [
            "'Colombian' es nacionalidad, necesitas 'Colombia' (país) después de 'from'",
            "Falta 'the'",
            "Debe ser 'I do from'"
          ],
          a: "'Colombian' es nacionalidad, necesitas 'Colombia' (país) después de 'from'",
          tip: "'FROM' + country name! 'I am FROM Colombia' OR 'I am Colombian' (without 'from')!"
        },
        {
          type: "input",
          q: "Corrige: 'We from Philippines and we are have 2 kids' → 'We ___ from the Philippines and we ___ 2 kids.'",
          a: "are, have",
          tip: "Origin = 'are FROM', possession = 'HAVE'. Also: 'THE Philippines' (needs article)!"
        },
        {
          type: "mc",
          q: "¿Cuál está CORRECTA?",
          options: [
            "I am 35 years old",
            "I have 35 years old",
            "I am have 35 years"
          ],
          a: "I am 35 years old",
          successtip: "✓ PERFECT! Age mastery! 🎂 'I AM' + age, never 'have'! Burned into memory now!"
        }
      ]
    },

    // SET 10: Real Situations
    {
      id: "m4-u1-set10",
      title: "Set 10: Real-Life Scenarios",
      questions: [
        {
          type: "mc",
          q: "Citizenship test interview: 'I ___ applying to become a Canadian citizen. I ___ here for 4 years.'",
          options: ["am, have been", "am, am", "do, am"],
          a: "am, have been",
          tip: "Advanced! 'I AM applying' (now), 'I HAVE BEEN' (duration). B1 level!"
        },
        {
          type: "input",
          q: "Meeting mortgage broker: 'I ___ interested in buying my first home. I ___ a permanent resident and I ___ working full-time.'",
          a: "am, am, am",
          successtip: "✓ HOMEOWNERSHIP! Canadian dream! 🏠 Three 'am's showing: interest, status, employment. Lender loves this!"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy buscando patrocinar a mi familia'",
          words: ["I", "am", "looking", "to", "sponsor", "my", "family."],
          a: "I am looking to sponsor my family.",
          tip: "'Looking to' = planning to. Immigration goal! Family reunification!"
        },
        {
          type: "match",
          q: "Match immigrant milestone to statement:",
          pairs: [
            { left: "Getting PR card", right: "I am now a permanent resident" },
            { left: "First job offer", right: "I am excited to start working" },
            { left: "Language test", right: "I am preparing for IELTS" }
          ],
          a: "completed",
          successtip: "✓ IMMIGRANT JOURNEY! Milestones! 🎯 PR → Job → Language → Citizenship. Each step = new identity!"
        },
        {
          type: "mc",
          q: "Parent-teacher meeting: 'I ___ Pedro's father. I ___ here to discuss his progress.'",
          options: ["am, am", "am, do", "do, am"],
          a: "am, am",
          tip: "Relationship = TO BE ('I am father'), purpose = TO BE ('I am here'). Both identity!"
        },
        {
          type: "input",
          q: "Family phone call home: 'Hi mama! I ___ good, I ___ healthy, and I ___ saving money to bring you here.'",
          a: "am, am, am",
          successtip: "✓ FAMILY CALL! Emotional! 😢💰 Every immigrant's promise: bring family! You got this!"
        },
        {
          type: "order",
          q: "Ordena (Emergency room): 'Soy el esposo de Maria Lopez, ella está enferma'",
          words: ["I", "am", "Maria", "Lopez's", "husband,", "she", "is", "sick."],
          a: "I am Maria Lopez's husband, she is sick.",
          tip: "Possessive 's! Maria Lopez'S husband. Emergency info must be clear!"
        },
        {
          type: "mc",
          q: "Job interview closing: 'I ___ very interested in this position and I ___ confident I can contribute to the team.'",
          options: ["am, am", "am, do", "do, am"],
          a: "am, am",
          successtip: "✓ CLOSING STRONG! Interview power! 💼 'Interested' + 'confident' = adjectives = TO BE! Hired!"
        },
        {
          type: "input",
          q: "Community center: 'Hi, I ___ new to the neighborhood. I ___ looking for ESL classes.'",
          a: "am, am",
          tip: "'New to' = recent arrival. 'Looking for' = searching. Both use 'am'!"
        },
        {
          type: "mc",
          q: "Perfect self-introduction for networking event:",
          options: [
            "Hi! I'm Carlos from Mexico, I'm 35, and I'm a project manager at Bell Canada. Nice to meet you!",
            "I do Carlos, I have 35 years, I work Bell",
            "My name Carlos, my age 35, my work manager"
          ],
          a: "Hi! I'm Carlos from Mexico, I'm 35, and I'm a project manager at Bell Canada. Nice to meet you!",
          successtip: "✓ NETWORKING CHAMPION! Complete intro! 🤝 Name + origin + age + job + company + greeting = PERFECT!"
        }
      ]
    }
  ],

  // ===================================================================
// READING SET: Sofia's Professional Introduction
// ===================================================================
readingSet: {
  id: "m4-u1-reading",
  title: "Set 11: Reading Practice",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: Identity & Origins (preguntas 1-2)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**A New Start in Canada**

Hello! My name is Sofia Martinez. I am 29 years old and I am from El Salvador. I am originally from San Salvador, but I am now a permanent resident here in Ontario.`
    },
    {
      type: "mc",
      q: "¿De dónde es Sofia?",
      options: ["El Salvador", "Mississauga", "Ontario"],
      a: "El Salvador",
      tip: "'I am from' = origin country!",
      successtip: "✓ ORIGIN!\nShe is originally from El Salvador. 🇸🇻"
    },
    {
      type: "mc",
      q: "Según el texto, ¿cuántos años tiene Sofia?",
      options: ["29", "28", "30"],
      a: "29",
      successtip: "✓ AGE!\nShe is 29 years old. 🎂"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Career & Current Work (preguntas 3-4)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Professional Background**

I am a certified early childhood educator, and I am currently working at a daycare center in Mississauga. I am very excited about my career here in Canada!`
    },
    {
      type: "mc",
      q: "¿Cuál es la profesión de Sofia?",
      options: [
        "Early childhood educator",
        "Teacher",
        "Daycare owner"
      ],
      a: "Early childhood educator",
      successtip: "✓ ECE! Great career! 👶\nEarly Childhood Educator = high demand in Canada! Rewarding work!"
    },
    {
      type: "mc",
      q: "¿Dónde trabaja Sofia actualmente?",
      options: [
        "At a daycare center in Mississauga",
        "In San Salvador",
        "At a school in Ontario"
      ],
      a: "At a daycare center in Mississauga",
      successtip: "✓ CURRENT JOB!\nShe works at a daycare in Mississauga. 🏫"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Education & Future (preguntas 5-7)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Building a Future**

I am also studying for my Canadian ECE license. It is a very important step for me. I am a permanent resident now, and I feel very positive about everything.`
    },
    {
      type: "mc",
      q: "¿Qué está estudiando Sofia?",
      options: [
        "Her Canadian ECE license",
        "English classes",
        "University degree"
      ],
      a: "Her Canadian ECE license",
      tip: "Canadian credentials! Many immigrants recertify their professions here!",
      successtip: "✓ CERTIFICATION!\nLicensing is key for professional growth. 📚"
    },
    {
      type: "mc",
      q: "¿Cuál es el estatus migratorio de Sofia?",
      options: [
        "Permanent resident",
        "Citizen",
        "Temporary worker"
      ],
      a: "Permanent resident",
      successtip: "✓ PR STATUS! Immigration goal! 🍁\nPermanent Resident = path to citizenship! Major milestone!"
    },
    {
      type: "mc",
      q: "¿Cómo se siente Sofia sobre su carrera en Canadá?",
      options: [
        "Very excited",
        "Nervous",
        "Uncertain"
      ],
      a: "Very excited",
      successtip: "✓ EMOTIONS!\nShe is very excited and positive! 😊"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Language Analysis & Inference (preguntas 8-10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Reflection**

As you can see, Sofia's introduction uses many "am" statements to define her identity, her status, and her feelings. This is a classic example of a professional self-introduction in a Canadian context.`
    },
    {
      type: "mc",
      q: "¿Cuántas veces usa Sofia el verbo 'to be' (am) en su presentación?",
      options: ["8 veces", "5 veces", "3 veces"],
      a: "8 veces",
      tip: "Count them! 'am' appears 8 times for different purposes. Identity verb!",
      successtip: "✓ GRAMMAR COUNT!\nYou noticed the frequent use of the verb To Be. 🎯"
    },
    {
      type: "mc",
      q: "El texto es un ejemplo de:",
      options: [
        "Professional self-introduction",
        "Resume",
        "Casual conversation"
      ],
      a: "Professional self-introduction",
      successtip: "✓ NETWORKING TEMPLATE! Use it! 💼\nThis structure = perfect for events, LinkedIn, or interviews!"
    },
    {
      type: "mc",
      q: "Inference: ¿Por qué Sofia menciona que está estudiando su licencia canadiense?",
      options: [
        "Porque quiere trabajar legalmente con su profesión en Canadá",
        "Porque no sabe inglés",
        "Porque es obligatorio para todos"
      ],
      a: "Porque quiere trabajar legalmente con su profesión en Canadá",
      tip: "Credential recognition! Many professional licenses don't transfer automatically to Canada!",
      successtip: "✓ LICENSING!\nLegal certification is necessary for protected professions in Canada. 🏛️"
    }
  ]
},
  // REVIEW SET
  reviewSet: {
    id: "m4-u1-review",
    title: "Set 12: Unit Review",
    category: "review",
    questions: [
      {
        type: "mc",
        q: "¿Cuál verbo usas para hablar de tu NOMBRE?",
        options: ["To be (am/is/are)", "To do", "To have"],
        a: "To be (am/is/are)",
        tip: "Name = identity = TO BE! Review Set 1!"
      },
      {
        type: "mc",
        q: "¿Cuál verbo usas para hablar de tu EDAD en inglés?",
        options: ["To be (am/is/are)", "To have", "To do"],
        a: "To be (am/is/are)",
        successtip: "✓ TO BE! Not 'have'! 🎂 English = I AM 30, Spanish = Tengo 30. Different logic!"
      },
      {
        type: "match",
        q: "Match concept to correct verb:",
        pairs: [
          { left: "Your name", right: "I am Carlos" },
          { left: "Your age", right: "I am 35" },
          { left: "Your nationality", right: "I am Mexican" }
        ],
        a: "completed"
      },
      {
        type: "input",
        q: "Complete: 'I ___ Ana, I ___ 28 years old, and I ___ from Peru.'",
        a: "am, am, am",
        tip: "Three times 'am'! Name, age, origin = all TO BE!"
      },
      {
        type: "mc",
        q: "ERROR común de hispanohablantes:",
        options: [
          "'I have 30 years' en vez de 'I am 30 years old'",
          "'I is 30 years'",
          "'I 30 years old'"
        ],
        a: "'I have 30 years' en vez de 'I am 30 years old'",
        successtip: "✓ TOP ERROR! Now you know! 🎯 'Tengo' ≠ 'I have' for age. Locked in memory!"
      },
      {
        type: "order",
        q: "Ordena presentación completa: 'Soy Roberto Silva, tengo 32 años, soy de Colombia y soy ingeniero'",
        words: ["I", "am", "Roberto", "Silva,", "I", "am", "32", "years", "old,", "I", "am", "from", "Colombia,", "and", "I", "am", "an", "engineer."],
        a: "I am Roberto Silva, I am 32 years old, I am from Colombia, and I am an engineer.",
        tip: "Master sentence! All identity info = all TO BE!"
      },
      {
        type: "mc",
        q: "Para presentarte en una ENTREVISTA, mejor opción:",
        options: [
          "Good morning, my name is Carlos Rodriguez and I am 35 years old",
          "Hey! I'm Carlos, I'm 35",
          "I do Carlos, I have 35 years"
        ],
        a: "Good morning, my name is Carlos Rodriguez and I am 35 years old",
        successtip: "✓ FORMAL MASTERY! Interview ready! 💼 Full name + complete structure = professionalism!"
      },
      {
        type: "input",
        q: "Third person: 'My wife ___ Sandra, she ___ 30, and she ___ a nurse.'",
        a: "is, is, is",
        tip: "He/she/it = IS! Everything changes from 'am' to 'is' for third person!"
      },
      {
        type: "mc",
        q: "Después de 'from' usas:",
        options: [
          "Country name (Mexico, Colombia)",
          "Nationality (Mexican, Colombian)",
          "Either one is fine"
        ],
        a: "Country name (Mexico, Colombia)",
        tip: "'FROM' + country! 'I am FROM Mexico' ✓, 'I am FROM Mexican' ✗"
      },
      {
        type: "mc",
        q: "Complete: 'I ___ a certified electrician' - ¿Qué falta?",
        options: [
          "I AM a certified electrician",
          "I DO a certified electrician",
          "I HAVE a certified electrician"
        ],
        a: "I AM a certified electrician",
        successtip: "✓ UNIT COMPLETE! Job mastery! ⚡ Identity = TO BE! You can now introduce yourself perfectly in ANY context! 🎉🇨🇦"
      }
    ]
  }
};
